import { theme } from "@op-str/op-component-library"
import { changeLocationHash, scrollToAnchor } from "@stg-oneportal/axs-frontend-helpers"
import { AsyncHandle } from "@stg-oneportal/corets-use-async"
import React, { ReactElement, RefObject, useEffect, useState } from "react"
import styled, { css } from "styled-components"
import { ExpandComponent, ExpandComponentItem } from "../../api/types/ExpandComponent"
import { generateQuestionId } from "../../helpers/generateQuestionId"
import {
  ButterCmsComponentSchema,
  HelpPageSchema,
  IntersectionUpdate,
  PageSlug,
  SectionOffsetTuple,
  ServicePageBanner,
} from "../../types"
import { ExpandItem } from "../Expand/ExpandItem"
import { HorizontalLine } from "../HorizontalLine"
import { ServiceBannerContainer } from "../ServiceBannerContainer"

export type ServicePageInfo = {
  servicePage: AsyncHandle<ServicePageBanner>
  servicePageSlug: PageSlug
  hide: () => void
  isShown: boolean
  helpBanner: ReactElement
}

export type HelpTabProps = {
  pageResult: HelpPageSchema
  servicePageInfo?: ServicePageInfo
  singleSection?: boolean
  layoutRef?: RefObject<HTMLDivElement>
  expandQuestionSideEffects: (tabTitle: string, sectionValue: string, questionTitleValue: string) => void
  tabTitle: string
  openQuestionsIds?: string[]
  onToggleQuestionItem?: (questionId: string) => void
}

export const HelpTab = (props: HelpTabProps) => {
  const {
    pageResult,
    layoutRef,
    singleSection = false,
    tabTitle,
    openQuestionsIds = [],
    onToggleQuestionItem,
    servicePageInfo,
    expandQuestionSideEffects,
  } = props
  const [activeSectionId, setActiveSectionId] = useState(window.location.hash.replace("#", ""))
  const [visibleSectionTitles, setVisibleSectionTitles] = useState<SectionOffsetTuple[]>([])
  const [intersectionUpdates, setIntersectionUpdates] = useState<IntersectionUpdate[]>([])

  const handleToggleExpandItem = (
    isOpen: boolean,
    section: ButterCmsComponentSchema<ExpandComponent>,
    item: ExpandComponentItem
  ) => {
    if (isOpen) expandQuestionSideEffects(tabTitle, section.fields.expand_section_title, item.expand_item_title)
    onToggleQuestionItem && onToggleQuestionItem(generateQuestionId(pageResult.slug, item.expand_item_title))
  }

  const observer = new IntersectionObserver(
    (observees) => {
      setIntersectionUpdates(
        observees.map((observee) => {
          return {
            sectionId: observee.target.id,
            sectionOffset: (observee.target as HTMLElement).offsetTop,
            intersect: observee.isIntersecting,
          }
        })
      )
    },
    {
      root: layoutRef?.current,
      threshold: 0,
    }
  )

  const observeSections = () => {
    pageResult.fields.sections.forEach((section) => {
      const sectionId = generateQuestionId(pageResult.slug ?? "", section.fields.expand_section_title)
      const sectionHTMLElement = document.getElementById(sectionId)

      if (sectionHTMLElement) observer.observe(sectionHTMLElement)
    })
  }

  useEffect(() => {
    observeSections()
  }, [pageResult])

  // Will be trigger by observer events, handling all the component logic
  useEffect(() => {
    let newVisibleSectionTitles = [...visibleSectionTitles]

    // Keep the list of visible sections up to date
    intersectionUpdates.forEach((intersectionUpdate) => {
      if (intersectionUpdate.intersect) {
        newVisibleSectionTitles.push({
          sectionId: intersectionUpdate.sectionId,
          sectionOffset: intersectionUpdate.sectionOffset,
        })
      } else {
        newVisibleSectionTitles = newVisibleSectionTitles.filter((visibleSectionElement) => {
          return visibleSectionElement.sectionId !== intersectionUpdate.sectionId
        })
      }
    })

    // Sort by ascending offset, smaller offset means higher on the page
    newVisibleSectionTitles.sort((a, b) => {
      if (a.sectionOffset < b.sectionOffset) {
        return -1
      } else if (a.sectionOffset > b.sectionOffset) {
        return 1
      } else {
        return 0
      }
    })

    if (newVisibleSectionTitles.length > 0) {
      setActiveSectionId(newVisibleSectionTitles[0].sectionId)
    }

    setVisibleSectionTitles(newVisibleSectionTitles)
  }, [intersectionUpdates])

  return (
    <HelpTabWithBanner>
      {!!servicePageInfo?.servicePage.result && servicePageInfo.isShown && (
        <CustomServiceBannerContainer>{servicePageInfo.helpBanner}</CustomServiceBannerContainer>
      )}
      <Root $singleSection={singleSection}>
        {!singleSection && (
          <LeftMenuBlock>
            <LeftMenu>
              {pageResult.fields.sections.map((section, index) => {
                return (
                  <CustomA
                    key={"link-" + index}
                    active={
                      generateQuestionId(pageResult.slug, section.fields.expand_section_title) === activeSectionId
                    }
                    onClick={() => {
                      changeLocationHash(generateQuestionId(pageResult.slug, section.fields.expand_section_title))
                      scrollToAnchor()
                    }}
                  >
                    {section.fields.expand_section_title}
                  </CustomA>
                )
              })}
            </LeftMenu>
          </LeftMenuBlock>
        )}
        <Content>
          {pageResult.fields.sections.map((section, index) => (
            <div key={"section-" + index}>
              {/* Section spacer only between sections */}
              {index !== 0 && <SectionSpacer />}
              <Section
                $singleSection={singleSection}
                id={generateQuestionId(pageResult.slug, section.fields.expand_section_title)}
              >
                {!singleSection && <SectionTitle>{section.fields.expand_section_title}</SectionTitle>}
                {section.fields.expand_item.map((item, expandIndex) => {
                  const questionId = generateQuestionId(pageResult.slug, item.expand_item_title)

                  return (
                    <div key={"expandItems-" + expandIndex} id={questionId}>
                      {/* Horizontal line only between items */}
                      {expandIndex !== 0 && <HorizontalLine />}
                      <CustomExpandItem
                        title={item.expand_item_title}
                        content={item.expand_item_content}
                        isForceOpen={openQuestionsIds.includes(questionId)}
                        onToggle={(isOpen) => handleToggleExpandItem(isOpen, section, item)}
                      />
                    </div>
                  )
                })}
              </Section>
              {index == pageResult.fields.sections.length - 1 && <LastSectionSpacer />}
            </div>
          ))}
        </Content>
      </Root>
    </HelpTabWithBanner>
  )
}

const HelpTabWithBanner = styled.div`
  display: flex;
  flex-direction: column;

  margin: ${theme.spacing.l} ${theme.spacing.lx} 0 ${theme.spacing.lx};
`

const Root = styled.div<{ $singleSection: boolean }>`
  ${(p) =>
    !p.$singleSection &&
    css`
      display: flex;
      gap: ${theme.spacing.lx};
    `}
`

const CustomServiceBannerContainer = styled(ServiceBannerContainer)`
  margin-top: ${theme.spacing.l};
  margin-bottom: 0;
  padding: ${theme.spacing.lx};
`

const LeftMenuBlock = styled.div`
  padding: ${theme.spacing.xl} ${theme.spacing.lx};
  min-width: 240px;
`

const LeftMenu = styled.div`
  position: sticky;
  top: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lx};
`

const CustomA = styled.a<{ active: boolean }>`
  && {
    cursor: pointer;
    font-family: ${theme.font.bold};
    color: ${theme.color.primary};
    line-height: ${theme.lineHeight.m};
  }

  // Only underline the active section
  text-decoration: none;
  ${(p) =>
    p.active &&
    css`
      text-decoration: underline;
    `}
`

const Content = styled.div`
  width: 100%;
`

const Section = styled.div<{ $singleSection: boolean }>`
  ${(p) =>
    !p.$singleSection &&
    css`
      padding-top: ${theme.spacing.xl};
    `}
`

const SectionTitle = styled.span`
  font-size: ${theme.fontSize.xxl};
  line-height: ${theme.lineHeight.l};
  font-family: ${theme.font.bold};
`

const SectionSpacer = styled.div`
  height: ${theme.spacing.xxl};
`

const LastSectionSpacer = styled.div`
  height: 96px;
`

const CustomExpandItem = styled(ExpandItem)`
  & img {
    max-width: 850px;
  }
`
